import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import Cookies from "js-cookie";
import PublicLayout from "../../layouts/PublicLayout";
import PrivateLayout from "../../layouts/PrivateLayout";
import PaymentForm from "../../components/PaymentForm";
import { useCart } from "../../context/CartContext";
import useLanguage from "../../utils/useLanguage";
import "@fortawesome/fontawesome-free/css/all.min.css";

const BGN_TO_EUR = 1.95583;
const toEur = (bgn) => (bgn / BGN_TO_EUR).toFixed(2);

// Initialise Stripe once (outside component to avoid re-creating on re-renders)
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080";

const t = {
  en: {
    bgnLabel: "lv.",
    title: "Your Cart",
    empty: "Your cart is empty",
    emptyDesc: "Looks like you haven't added anything yet.",
    continueShopping: "Continue Shopping",
    remove: "Remove",
    quantity: "Qty",
    subtotal: "Subtotal",
    shipping: "Shipping",
    shippingNote: "Calculated at checkout",
    total: "Total",
    placeOrder: "Continue to Payment",
    orderSuccess: "Payment Successful!",
    orderSuccessDesc: "Thank you! Your payment was confirmed. We'll contact you on Discord shortly.",
    backToShop: "Back to Shop",
    items: "items",
    orderSummary: "Order Summary",
    each: "each",
    name: "Full Name",
    email: "Email",
    address: "Delivery Address",
    phone: "Phone Number",
    details: "Order Details",
    namePlaceholder: "Your full name",
    emailPlaceholder: "your@email.com",
    addressPlaceholder: "Street, City, Postal Code",
    phonePlaceholder: "+359 ...",
    required: "Please fill in all fields",
    paymentTitle: "Secure Payment",
    paymentSubtitle: "Pay by card or Revolut",
    paymentError: "Could not initialise payment. Please try again.",
    step1: "Details",
    step2: "Payment",
  },
  bg: {
    bgnLabel: "лв.",
    title: "Вашата Кошница",
    empty: "Кошницата ви е празна",
    emptyDesc: "Изглежда все още не сте добавили нищо.",
    continueShopping: "Продължи пазаруването",
    remove: "Премахни",
    quantity: "Бр.",
    subtotal: "Междинна сума",
    shipping: "Доставка",
    shippingNote: "Изчислява се при поръчка",
    total: "Общо",
    placeOrder: "Продължи към плащане",
    orderSuccess: "Плащането е успешно!",
    orderSuccessDesc: "Благодарим ви! Плащането ви е потвърдено. Ще се свържем с вас в Discord скоро.",
    backToShop: "Обратно към магазина",
    items: "артикула",
    orderSummary: "Резюме на поръчката",
    each: "бр.",
    name: "Три имена",
    email: "Имейл",
    address: "Адрес за доставка",
    phone: "Телефонен номер",
    details: "Детайли за поръчката",
    namePlaceholder: "Вашите три имена",
    emailPlaceholder: "вашият@имейл.com",
    addressPlaceholder: "Улица, Град, Пощенски код",
    phonePlaceholder: "+359 ...",
    required: "Моля, попълнете всички полета",
    paymentTitle: "Сигурно плащане",
    paymentSubtitle: "Платете с карта или Revolut",
    paymentError: "Неуспешна инициализация на плащането. Опитайте отново.",
    step1: "Детайли",
    step2: "Плащане",
  },
};

// Steps: "details" → "payment" → "success"
function Cart() {
  const { lang } = useLanguage();
  const s = t[lang];
  const { cartItems, removeFromCart, updateQuantity, totalPrice, totalItems, clearCart } = useCart();
  const navigate = useNavigate();
  const isLoggedIn = !!Cookies.get("jwt");
  const Wrapper = isLoggedIn ? PrivateLayout : PublicLayout;

  const [form, setForm] = useState({ name: "", email: "", address: "", phone: "" });
  const [error, setError] = useState("");
  const [step, setStep] = useState("details"); // "details" | "payment" | "success"
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);

  const totalEur = toEur(totalPrice);

  // Step 1 → Step 2: validate form, call backend to create PaymentIntent
  const handleContinueToPayment = async () => {
    if (!form.name || !form.email || !form.address || !form.phone) {
      setError(s.required);
      return;
    }
    setError("");
    setPaymentLoading(true);

    try {
      const amountEurCents = Math.round((totalPrice / BGN_TO_EUR) * 100);

      const res = await fetch(`${API_URL}/payment/create-intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amountEurCents,
          customerName: form.name,
          customerEmail: form.email,
          address: form.address,
          phone: form.phone,
        }),
      });

      if (!res.ok) throw new Error("Backend error");

      const { clientSecret: secret } = await res.json();
      setClientSecret(secret);
      setStep("payment");
    } catch {
      setError(s.paymentError);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Step 2 → Step 3: Stripe payment confirmed
  const handlePaymentSuccess = () => {
    clearCart();
    setStep("success");
  };

  // Step 2 → Step 1: user goes back
  const handleBackToDetails = () => {
    setStep("details");
    setClientSecret(null);
  };

  // Stripe Elements appearance — dark theme matching the site
  const stripeAppearance = {
    theme: "night",
    variables: {
      colorPrimary: "#3b82f6",
      colorBackground: "#1f2937",
      colorText: "#f9fafb",
      colorDanger: "#f87171",
      fontFamily: "inherit",
      borderRadius: "8px",
    },
    rules: {
      ".Input": {
        border: "1px solid #374151",
        backgroundColor: "#111827",
      },
      ".Input:focus": {
        border: "1px solid #3b82f6",
        boxShadow: "none",
      },
      ".Tab": {
        border: "1px solid #374151",
        backgroundColor: "#111827",
        padding: "6px 8px",
        fontSize: "11px",
      },
      ".Tab--selected": {
        border: "1px solid #3b82f6",
        backgroundColor: "#1e3a5f",
      },
      ".TabLabel": {
        fontSize: "11px",
      },
    },
  };

  return (
    <Wrapper>
      <div className="min-h-screen flex flex-col">
        <div
          className="fixed inset-0 -z-10"
          style={{
            backgroundImage: "url('/wow-wallpaper.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="fixed inset-0 bg-black/75 -z-10 pointer-events-none" />

      <div className="relative z-10 flex flex-col min-h-screen">
        <main className="flex-1 px-4 sm:px-6 lg:px-8 py-10">
          <div className="max-w-5xl mx-auto">

            {/* ── SUCCESS ── */}
            {step === "success" ? (
              <div className="flex flex-col items-center justify-center py-24 text-center">
                <div className="w-20 h-20 rounded-full bg-green-600 flex items-center justify-center mb-6 shadow-lg shadow-green-900/40">
                  <i className="fas fa-check text-white text-3xl"></i>
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white font-serif mb-3">{s.orderSuccess}</h2>
                <p className="text-gray-300 text-base max-w-md mb-8">{s.orderSuccessDesc}</p>
                <button
                  onClick={() => navigate("/shop")}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300"
                >
                  <i className="fas fa-store"></i>
                  <span>{s.backToShop}</span>
                </button>
              </div>
            ) : (
              <>
                {/* Page title + step indicator */}
                <div className="flex items-center space-x-3 mb-8">
                  <button
                    onClick={step === "payment" ? handleBackToDetails : () => navigate("/shop")}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    <i className="fas fa-arrow-left text-lg"></i>
                  </button>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white font-serif">{s.title}</h1>
                  {totalItems > 0 && (
                    <span className="bg-blue-600 text-white text-sm font-bold px-2.5 py-0.5 rounded-full">
                      {totalItems} {s.items}
                    </span>
                  )}

                  {/* Step breadcrumb (only when cart has items) */}
                  {cartItems.length > 0 && (
                    <div className="ml-auto flex items-center space-x-2 text-xs font-medium">
                      <span className={step === "details" ? "text-blue-400" : "text-gray-500"}>
                        1. {s.step1}
                      </span>
                      <i className="fas fa-chevron-right text-gray-600 text-xs"></i>
                      <span className={step === "payment" ? "text-blue-400" : "text-gray-500"}>
                        2. {s.step2}
                      </span>
                    </div>
                  )}
                </div>

                {/* ── EMPTY STATE ── */}
                {cartItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-24 text-center">
                    <div className="w-20 h-20 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mb-6">
                      <i className="fas fa-shopping-basket text-gray-500 text-3xl"></i>
                    </div>
                    <h2 className="text-xl font-semibold text-white mb-2">{s.empty}</h2>
                    <p className="text-gray-400 mb-8">{s.emptyDesc}</p>
                    <button
                      onClick={() => navigate("/shop")}
                      className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-xl font-semibold transition-all duration-300"
                    >
                      <i className="fas fa-store"></i>
                      <span>{s.continueShopping}</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col lg:flex-row gap-6">

                    {/* ── LEFT COLUMN ── */}
                    <div className="flex-1 space-y-4">

                      {/* Cart items (always visible) */}
                      {cartItems.map((item) => (
                        <div
                          key={item.id}
                          className="bg-gray-900/90 border border-gray-700 rounded-xl p-4 flex items-center gap-4"
                        >
                          <div className="w-20 h-20 bg-white rounded-lg flex-shrink-0 overflow-hidden">
                            <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1.5" />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h3 className="text-white font-semibold text-sm sm:text-base truncate">{item.name}</h3>
                            <p className="text-blue-400 font-bold text-sm mt-0.5">
                              {item.rawPrice.toFixed(2)} {s.bgnLabel}{" "}
                              <span className="text-gray-500 font-normal">/ {toEur(item.rawPrice)} €</span>
                            </p>
                          </div>

                          {/* Quantity controls — disabled on payment step */}
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={step === "payment"}
                              className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white flex items-center justify-center transition-colors"
                            >
                              <i className="fas fa-minus text-xs"></i>
                            </button>
                            <span className="text-white font-bold w-6 text-center">{item.quantity}</span>
                            <button
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={step === "payment"}
                              className="w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white flex items-center justify-center transition-colors"
                            >
                              <i className="fas fa-plus text-xs"></i>
                            </button>
                          </div>

                          <div className="flex flex-col items-end flex-shrink-0 w-24">
                            <span className="text-white font-bold text-sm">{(item.rawPrice * item.quantity).toFixed(2)} {s.bgnLabel}</span>
                            <span className="text-gray-500 text-xs">{toEur(item.rawPrice * item.quantity)} €</span>
                          </div>

                          <button
                            onClick={() => removeFromCart(item.id)}
                            disabled={step === "payment"}
                            className="text-gray-500 hover:text-red-400 disabled:opacity-40 transition-colors flex-shrink-0 ml-1"
                            title={s.remove}
                          >
                            <i className="fas fa-trash text-sm"></i>
                          </button>
                        </div>
                      ))}

                      {/* ── STEP 1: Order details form ── */}
                      {step === "details" && (
                        <div className="bg-gray-900/90 border border-gray-700 rounded-xl p-5 mt-2">
                          <h2 className="text-white font-semibold text-base mb-4 flex items-center space-x-2">
                            <i className="fas fa-clipboard-list text-blue-400"></i>
                            <span>{s.details}</span>
                          </h2>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {[
                              { key: "name",    label: s.name,    placeholder: s.namePlaceholder,    type: "text" },
                              { key: "email",   label: s.email,   placeholder: s.emailPlaceholder,   type: "email" },
                              { key: "phone",   label: s.phone,   placeholder: s.phonePlaceholder,   type: "tel" },
                              { key: "address", label: s.address, placeholder: s.addressPlaceholder, type: "text" },
                            ].map(({ key, label, placeholder, type }) => (
                              <div key={key} className={key === "address" ? "sm:col-span-2" : ""}>
                                <label className="block text-gray-400 text-xs font-medium mb-1.5 uppercase tracking-wide">
                                  {label}
                                </label>
                                <input
                                  type={type}
                                  placeholder={placeholder}
                                  value={form[key]}
                                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                                  className="w-full bg-gray-800 border border-gray-600 rounded-lg px-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                                />
                              </div>
                            ))}
                          </div>
                          {error && (
                            <p className="text-red-400 text-sm mt-3 flex items-center space-x-1">
                              <i className="fas fa-exclamation-circle"></i>
                              <span>{error}</span>
                            </p>
                          )}
                        </div>
                      )}

                      {/* ── STEP 2: Stripe Payment Element ── */}
                      {step === "payment" && clientSecret && (
                        <div className="bg-gray-900/90 border border-gray-700 rounded-xl p-5 mt-2">
                          <h2 className="text-white font-semibold text-base mb-1 flex items-center space-x-2">
                            <i className="fas fa-lock text-blue-400"></i>
                            <span>{s.paymentTitle}</span>
                          </h2>
                          <p className="text-gray-400 text-xs mb-5">{s.paymentSubtitle}</p>

                          <Elements
                            stripe={stripePromise}
                            options={{
                              clientSecret,
                              appearance: stripeAppearance,
                            }}
                          >
                            <PaymentForm
                              totalEur={totalEur}
                              onSuccess={handlePaymentSuccess}
                              onBack={handleBackToDetails}
                              lang={lang}
                            />
                          </Elements>
                        </div>
                      )}
                    </div>

                    {/* ── RIGHT COLUMN: Order summary ── */}
                    <div className="lg:w-80 flex-shrink-0">
                      <div className="bg-gray-900/90 border border-gray-700 rounded-xl p-5 sticky top-6">
                        <h2 className="text-white font-semibold text-base mb-5 flex items-center space-x-2">
                          <i className="fas fa-receipt text-blue-400"></i>
                          <span>{s.orderSummary}</span>
                        </h2>

                        <div className="space-y-3 mb-5">
                          {cartItems.map((item) => (
                            <div key={item.id} className="flex justify-between text-sm">
                              <span className="text-gray-400 truncate pr-2">
                                {item.name} <span className="text-gray-600">×{item.quantity}</span>
                              </span>
                              <div className="flex flex-col items-end">
                                <span className="text-white font-medium whitespace-nowrap">
                                  {(item.rawPrice * item.quantity).toFixed(2)} {s.bgnLabel}
                                </span>
                                <span className="text-gray-500 text-xs">{toEur(item.rawPrice * item.quantity)} €</span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="border-t border-gray-700 pt-4 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">{s.subtotal}</span>
                            <div className="flex flex-col items-end">
                              <span className="text-white">{totalPrice.toFixed(2)} {s.bgnLabel}</span>
                              <span className="text-gray-500 text-xs">{totalEur} €</span>
                            </div>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">{s.shipping}</span>
                            <span className="text-gray-400 italic text-xs">{s.shippingNote}</span>
                          </div>
                        </div>

                        <div className="border-t border-gray-700 mt-4 pt-4 flex justify-between">
                          <span className="text-white font-bold text-base">{s.total}</span>
                          <div className="flex flex-col items-end">
                            <span className="text-blue-400 font-bold text-lg">{totalPrice.toFixed(2)} {s.bgnLabel}</span>
                            <span className="text-gray-400 text-sm">{totalEur} €</span>
                          </div>
                        </div>

                        {/* CTA — only on details step; payment step uses PaymentForm's own button */}
                        {step === "details" && (
                          <button
                            onClick={handleContinueToPayment}
                            disabled={paymentLoading}
                            className="mt-5 w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed active:scale-95 text-white py-3 rounded-xl font-bold text-sm transition-all duration-300 shadow-lg shadow-blue-900/30 flex items-center justify-center space-x-2"
                          >
                            {paymentLoading ? (
                              <>
                                <i className="fas fa-spinner fa-spin"></i>
                                <span>Loading…</span>
                              </>
                            ) : (
                              <>
                                <i className="fas fa-shield-alt"></i>
                                <span>{s.placeOrder}</span>
                              </>
                            )}
                          </button>
                        )}

                        {step === "details" && (
                          <button
                            onClick={() => navigate("/shop")}
                            className="mt-3 w-full text-gray-400 hover:text-white text-sm py-2 transition-colors flex items-center justify-center space-x-2"
                          >
                            <i className="fas fa-arrow-left text-xs"></i>
                            <span>{s.continueShopping}</span>
                          </button>
                        )}

                        {/* Secure badge */}
                        <div className="mt-4 flex items-center justify-center space-x-1.5 text-gray-600 text-xs">
                          <i className="fas fa-lock text-xs"></i>
                          <span>Secured by Stripe</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>
      </div>
    </Wrapper>
  );
}

export default Cart;
